import React, { useState } from 'react';
import { Box, Alert, Fab, SwipeableDrawer, Typography, useMediaQuery, useTheme } from '@mui/material';
import ListIcon from '@mui/icons-material/List';
import { useAppContext } from '../context/AppContext';
import FleetCanopyList from '../components/FleetCanopyList';
import MultiCanopyTelemetryView from '../components/MultiCanopyTelemetryView';

/**
 * FleetView component provides a multi-canopy comparison interface.
 * Users can select up to 3 canopies and view their telemetry data simultaneously.
 * On mobile, the canopy list is a slide-out drawer from the right edge.
 */
function FleetView() {
  const { 
    customerContext, 
    fleetContext
  } = useAppContext();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State for selected canopies in the fleet view
  const [selectedCanopies, setSelectedCanopies] = useState([]);
  const [canopyNames, setCanopyNames] = useState({});
  // State for mobile drawer
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Validate that we have the required context for fleet view
  const hasRequiredContext = customerContext && fleetContext;

  // Handle canopy selection from FleetCanopyList
  const handleCanopySelection = (canopyIds, namesMap = {}) => {
    setSelectedCanopies(canopyIds);
    if (namesMap && typeof namesMap === 'object') {
      setCanopyNames((prev) => ({ ...prev, ...namesMap }));
    }
  };

  if (!hasRequiredContext) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Please select a customer and fleet to view the fleet comparison interface.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Telemetry View (takes full width on mobile) */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <MultiCanopyTelemetryView
            selectedCanopies={selectedCanopies}
            canopyNames={canopyNames}
          />
        </Box>

        {/* Canopy List — desktop: inline sidebar, mobile: slide-out drawer */}
        {isMobile ? (
          <>
            {/* FAB to open canopy list drawer */}
            <Fab
              color="primary"
              size="medium"
              aria-label="select canopies"
              onClick={() => setDrawerOpen(true)}
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 1000
              }}
            >
              <ListIcon />
            </Fab>

            <SwipeableDrawer
              anchor="right"
              open={drawerOpen}
              onOpen={() => setDrawerOpen(true)}
              onClose={() => setDrawerOpen(false)}
              sx={{
                '& .MuiDrawer-paper': {
                  width: 300,
                  maxWidth: '85vw'
                }
              }}
            >
              <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Select Canopies
                </Typography>
              </Box>
              <FleetCanopyList onCanopySelection={handleCanopySelection} />
            </SwipeableDrawer>
          </>
        ) : (
          /* Desktop: fixed-width inline canopy list (unchanged) */
          <Box sx={{ width: 300, borderLeft: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            <FleetCanopyList onCanopySelection={handleCanopySelection} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default FleetView; 